// src/data/locationsClient.js
//
// Real client for /api/locations. Wraps the 7 server routes (GET list,
// GET one, POST, PATCH, DELETE soft, PATCH /health, PATCH /stage) and
// honors the contract in src/data/CONTRACT.md exactly:
//
//   - All methods async, return camelCase domain objects.
//   - Reads go through services/locationCache.js (5 min TTL).
//   - Writes invalidate every portfolio cache via services/cacheBus.js
//     (locationCache + reportsCache fan-out).
//   - Writes invalidate the entire cache, then call recordAudit() with
//     the right action / before / after.
//   - Errors are typed instances from src/data/errors.js.
//   - getLocation() returns null on 404 (per contract); other 404s on
//     write paths throw NotFoundError.
//
// The server already returns / accepts camelCase, so there is no field
// remapping at this boundary - just a thin transport layer.

import {
  AuthError,
  ConflictError,
  NetworkError,
  NotFoundError,
  ValidationError,
  errorFromResponse,
} from "./errors.js";
import {
  PORTFOLIO_KEY,
  get as cacheGet,
  locationKey,
  set as cacheSet,
} from "../services/locationCache.js";
import {
  invalidatePortfolioCaches as invalidateAll,
  emitCommandIndexStale,
} from "../services/cacheBus.js";
import { recordAudit } from "../services/auditLog.js";
import { log } from "../utils/logger.js";
import { REGIONS } from "../app/tokens.js";

// ─── Constants ────────────────────────────────────────────────────────────

const BASE         = "/api/locations";
const ID_REGEX     = /^(AMER|EMEA|APAC)-[A-Z]{3}-\d{2}$/;
const VALID_HEALTH = new Set(["green", "yellow", "red"]);
// Derived from REGIONS (tokens.js SSOT) so a new region added there
// automatically tightens this guard without a manual update here.
const VALID_REGION = new Set(REGIONS);
let activeAttendanceSource = "unknown";
// LSE-VACATED: 'vacated' joins the enum between 'prospecting' and 'closed' to
// mirror the SSOT ordering in src/app/tokens.js STAGES (Active → Pipeline →
// Inactive). Set membership is order-agnostic, but we mirror the SSOT order
// so a future reader can scan the two side-by-side without surprise. Client
// gate must stay in lockstep with server STAGES Set and the locations.stage
// CHECK constraint — drift here silently 400s every vacated write before it
// leaves the browser. (L52)
// 'dormant' = temporarily closed, Active group, not scored. Must be listed here
// so addLocation / updateLocation / updateStage accept it without a 400.
const VALID_STAGE  = new Set(["draft", "build", "operational", "dormant", "vacated", "closed"]);

// Iterates the list returned by the API and warns for each row whose `region`
// field is missing or not in REGIONS. We warn rather than throw so a single
// bad row from the server doesn't crash the entire portfolio view — but the
// warning is loud enough that it surfaces immediately in logs and in tests.
// Per principle A2 ("no silent fallbacks"), this must never silently discard.
function warnUnknownRegions(list) {
  for (const loc of list) {
    if (!VALID_REGION.has(loc.region)) {
      log.warn("locationsClient: unrecognised region on incoming row", {
        id: loc.id,
        region: loc.region,
        expected: REGIONS,
      });
    }
  }
}

// Throws ValidationError if id is missing or doesn't match REGION-CITYCODE-SEQ
// where REGION is one of the three allowed regions. Used as the pre-flight
// gate on every write path so we never round-trip a malformed id to the server.
function assertValidId(method, id) {
  if (!id || typeof id !== "string") {
    throw new ValidationError(`${method}: id required`, { field: "id" });
  }
  if (!ID_REGEX.test(id)) {
    throw new ValidationError(
      `${method}: invalid id "${id}" - must match REGION-CITYCODE-SEQ (e.g. AMER-SFO-01) where REGION is AMER, EMEA, or APAC`,
      { field: "id", got: id }
    );
  }
}

// ─── Internal: fetch wrapper that throws typed errors ─────────────────────

async function jsonRequest(url, init) {
  let res;
  try {
    res = await fetch(url, {
      ...init,
      credentials: "include",
      headers: { "Content-Type": "application/json", ...(init?.headers || {}) },
    });
  } catch (err) {
    // Pure transport failure (DNS, offline, CORS preflight). Wrap.
    log.error("locationsClient: network failure", { url, err: String(err) });
    throw new NetworkError(`Failed to reach ${url}`, err);
  }
  if (!res.ok) {
    const e = await errorFromResponse(res);
    log.warn("locationsClient: non-2xx", { url, status: res.status, name: e.name });
    throw e;
  }
  // 200 with empty body is technically valid; guard JSON parse.
  if (res.status === 204) return null;
  try {
    return await res.json();
  } catch (err) {
    throw new NetworkError(`Malformed JSON from ${url}`, err);
  }
}

// ─── Reads ────────────────────────────────────────────────────────────────

/**
 * @returns {Promise<Array<object>>}  Full portfolio (server filters soft-deletes).
 * @throws {NetworkError | AuthError}
 */
export async function getLocations() {
  const cacheKey = activeAttendanceSource === "unknown"
    ? PORTFOLIO_KEY
    : `${PORTFOLIO_KEY}:${activeAttendanceSource}`;
  const cached = cacheGet(cacheKey);
  if (cached) {
    log.api("locationsClient.getLocations cache hit", { count: cached.length });
    return cached;
  }
  const list = await jsonRequest(BASE, { method: "GET" });
  warnUnknownRegions(list);
  activeAttendanceSource = list.find((row) => row.attendanceSourceMode)?.attendanceSourceMode ?? "unknown";
  cacheSet(
    activeAttendanceSource === "unknown" ? PORTFOLIO_KEY : `${PORTFOLIO_KEY}:${activeAttendanceSource}`,
    list,
  );
  // Intentionally NOT warming per-id cache slots from list rows. The
  // list endpoint returns lightweight rows without embedded children
  // (leases / criticalDates / watchItems / etc.), so warming would
  // poison getLocation(id) into returning a child-less row from cache
  // forever. Detail rows are cached only when getLocation() actually
  // fetches them.
  log.api("locationsClient.getLocations fetched", { count: list.length });
  return list;
}

/**
 * @param {string} id  REGION-CITYCODE-SEQ
 * @returns {Promise<object|null>}  null on 404 per contract.
 * @throws {NetworkError | AuthError}
 */
export async function getLocation(id) {
  if (!id || typeof id !== "string") {
    // Contract says only Network/Auth throw here; treat unknown id as null.
    return null;
  }
  const key = activeAttendanceSource === "unknown"
    ? locationKey(id)
    : `${locationKey(id)}:${activeAttendanceSource}`;
  const cached = cacheGet(key);
  if (cached !== undefined) {
    log.api("locationsClient.getLocation cache hit", { id });
    return cached;
  }
  try {
    const loc = await jsonRequest(`${BASE}/${encodeURIComponent(id)}`, { method: "GET" });
    if (loc !== null) warnUnknownRegions([loc]);
    activeAttendanceSource = loc?.attendanceSourceMode ?? activeAttendanceSource;
    const modeKey = activeAttendanceSource === "unknown"
      ? locationKey(id)
      : `${locationKey(id)}:${activeAttendanceSource}`;
    cacheSet(modeKey, loc);
    return loc;
  } catch (err) {
    if (err instanceof NotFoundError) {
      // Per contract: getLocation returns null on miss, never throws NotFound.
      cacheSet(key, null);
      return null;
    }
    if (err instanceof ValidationError) {
      // Bad ID format. Treat as miss for the caller.
      log.warn("locationsClient.getLocation: invalid id", { id, msg: err.message });
      return null;
    }
    throw err;
  }
}

// ─── Writes ───────────────────────────────────────────────────────────────

function writesCapacity(payload) {
  if (!payload || typeof payload !== "object") return false;
  if (["onlineDate", "exitDate", "reopenDate"].some((key) => (
    Object.prototype.hasOwnProperty.call(payload, key)
  ))) {
    return true;
  }
  if (!Object.prototype.hasOwnProperty.call(payload, "installedCapacity")) return false;
  if (payload.installedCapacity == null) return false;
  const seats = Number(payload.installedCapacity);
  return Number.isFinite(seats) && seats >= 0;
}

/**
 * @param {object} payload  NewLocation; id required, must match REGION-CITYCODE-SEQ.
 * @returns {Promise<object>}  Created Location.
 * @throws {NetworkError | AuthError | ValidationError | ConflictError}
 * Audit:  location.create
 */
export async function addLocation(payload) {
  if (!payload || typeof payload !== "object") {
    throw new ValidationError("addLocation: payload required");
  }
  assertValidId("addLocation", payload.id);
  if (payload.health != null && !VALID_HEALTH.has(payload.health)) {
    throw new ValidationError(`addLocation: invalid health "${payload.health}"`, { field: "health" });
  }
  if (payload.stage != null && !VALID_STAGE.has(payload.stage)) {
    throw new ValidationError(`addLocation: invalid stage "${payload.stage}"`, { field: "stage" });
  }
  const created = await jsonRequest(BASE, { method: "POST", body: JSON.stringify(payload) });
  invalidateAll();
  if (writesCapacity(payload)) {
    emitCommandIndexStale();
  }
  recordAudit({
    action:     "location.create",
    entityType: "location",
    entityId:   created.id,
    before:     null,
    after:      created,
  });
  return created;
}

/**
 * @param {string} id
 * @param {object} patch  Partial<Location>; shallow-merged on the server.
 * @returns {Promise<object>}  Updated Location.
 * @throws {NetworkError | AuthError | ValidationError | NotFoundError}
 * Audit:  location.update (with before/after when before is cached)
 */
export async function updateLocation(id, patch) {
  assertValidId("updateLocation", id);
  if (!patch || typeof patch !== "object") {
    throw new ValidationError("updateLocation: patch object required");
  }
  if (patch.health != null && !VALID_HEALTH.has(patch.health)) {
    throw new ValidationError(`updateLocation: invalid health "${patch.health}"`, { field: "health" });
  }
  if (patch.stage != null && !VALID_STAGE.has(patch.stage)) {
    throw new ValidationError(`updateLocation: invalid stage "${patch.stage}"`, { field: "stage" });
  }
  const before = cacheGet(locationKey(id)) ?? null; // best-effort; trigger has truth
  const changesCapacity = writesCapacity(patch);
  const after = await jsonRequest(`${BASE}/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
  invalidateAll();
  if (changesCapacity) emitCommandIndexStale();
  recordAudit({
    action:     "location.update",
    entityType: "location",
    entityId:   id,
    before,
    after,
  });
  return after;
}

/** Parent-only archive; explicit audit and feedback are owned by server and UI. */
export async function archiveLocation(id, reason) {
  assertValidId("archiveLocation", id);
  if (reason != null && typeof reason !== "string") {
    throw new ValidationError("Reason must be a string", { field: "reason" });
  }
  const result = await jsonRequest(`${BASE}/${encodeURIComponent(id)}/archive`, {
    method: "POST", body: JSON.stringify({ reason: reason?.trim() || null }),
  });
  invalidateAll();
  emitCommandIndexStale();
  // The server owns the single explicit archive event; no client duplicate.
  return result;
}

/** Legacy soft-delete compatibility for existing consumers. */
export async function deleteLocation(id) {
  assertValidId("deleteLocation", id);
  const before = cacheGet(locationKey(id)) ?? null;
  const result = await jsonRequest(`${BASE}/${encodeURIComponent(id)}`, { method: "DELETE" });
  invalidateAll();
  recordAudit({
    action:     "location.delete",
    entityType: "location",
    entityId:   id,
    before,
    after:      null,
  });
  return result;
}

/**
 * @param {string} id
 * @param {'green'|'yellow'|'red'} health
 * @returns {Promise<object>}  Updated Location.
 * @throws {NetworkError | AuthError | ValidationError | NotFoundError}
 * Audit:  location.update_health
 */
export async function updateHealth(id, health) {
  assertValidId("updateHealth", id);
  if (!VALID_HEALTH.has(health)) {
    throw new ValidationError(`Invalid health: ${health}`, { field: "health" });
  }
  const before = cacheGet(locationKey(id)) ?? null;
  const after = await jsonRequest(`${BASE}/${encodeURIComponent(id)}/health`, {
    method: "PATCH",
    body: JSON.stringify({ health }),
  });
  invalidateAll();
  recordAudit({
    action:     "location.update_health",
    entityType: "location",
    entityId:   id,
    before,
    after,
  });
  return after;
}

/**
 * @param {string} id
 * @param {'operational'|'dormant'|'build'|'vacated'|'closed'} stage
 * @returns {Promise<object>}  Updated Location.
 * @throws {NetworkError | AuthError | ValidationError | NotFoundError}
 * Audit:  location.update_stage
 */
export async function updateStage(id, stage) {
  assertValidId("updateStage", id);
  if (!VALID_STAGE.has(stage)) {
    throw new ValidationError(`Invalid stage: ${stage}`, { field: "stage" });
  }
  const before = cacheGet(locationKey(id)) ?? null;
  const after = await jsonRequest(`${BASE}/${encodeURIComponent(id)}/stage`, {
    method: "PATCH",
    body: JSON.stringify({ stage }),
  });
  invalidateAll();
  recordAudit({
    action:     "location.update_stage",
    entityType: "location",
    entityId:   id,
    before,
    after,
  });
  return after;
}

// ─── Photo write paths (slice 11b) ────────────────────────────────────────
//
// Multipart uploads. We do NOT use jsonRequest here because the body is
// FormData, not JSON, and forcing Content-Type: application/json would
// break multer parsing on the server. The error mapping is otherwise
// identical to jsonRequest.

const PHOTO_ALLOWED = new Set(["image/jpeg", "image/png", "image/webp"]);
const PHOTO_MAX_BYTES = 5 * 1024 * 1024;

/**
 * @param {string} id
 * @param {File | Blob} file  Multipart 'photo' field; type+size validated client-side.
 * @returns {Promise<object>}  Updated Location row, with new imageUrl.
 * @throws  {NetworkError | AuthError | ValidationError | NotFoundError}
 * Audit:  location.upload_photo
 */
export async function uploadLocationPhoto(id, file) {
  assertValidId("uploadLocationPhoto", id);
  if (!file) {
    throw new ValidationError("uploadLocationPhoto: file required", { field: "photo" });
  }
  // file.type is empty for some Blob constructions; only enforce when set.
  if (file.type && !PHOTO_ALLOWED.has(file.type)) {
    throw new ValidationError(
      `Unsupported file type: ${file.type}. Use JPEG, PNG, or WebP.`,
      { field: "photo", got: file.type },
    );
  }
  if (typeof file.size === "number" && file.size > PHOTO_MAX_BYTES) {
    const mb = (file.size / 1024 / 1024).toFixed(1);
    throw new ValidationError(
      `File too large (${mb} MB). Maximum is 5 MB.`,
      { field: "photo", bytes: file.size },
    );
  }

  const before = cacheGet(locationKey(id)) ?? null;
  const fd = new FormData();
  fd.append("photo", file);

  let res;
  try {
    res = await fetch(`${BASE}/${encodeURIComponent(id)}/photo`, {
      method: "POST",
      credentials: "include",
      body: fd,
      // Intentionally NO Content-Type header — the browser sets the
      // multipart boundary automatically and overriding it breaks multer.
    });
  } catch (err) {
    log.error("locationsClient: upload network failure", { id, err: String(err) });
    throw new NetworkError(`Failed to upload photo for ${id}`, err);
  }
  if (!res.ok) {
    const e = await errorFromResponse(res);
    log.warn("locationsClient: upload non-2xx", { id, status: res.status, name: e.name });
    throw e;
  }
  let after;
  try {
    after = await res.json();
  } catch (err) {
    throw new NetworkError(`Malformed JSON from photo upload`, err);
  }
  invalidateAll();
  recordAudit({
    action:     "location.upload_photo",
    entityType: "location",
    entityId:   id,
    before,
    after,
  });
  return after;
}

/**
 * @param {string} id
 * @returns {Promise<object>}  Updated Location row, imageUrl=null.
 * @throws  {NetworkError | AuthError | NotFoundError}
 * Audit:  location.delete_photo
 */
export async function deleteLocationPhoto(id) {
  assertValidId("deleteLocationPhoto", id);
  const before = cacheGet(locationKey(id)) ?? null;
  const after = await jsonRequest(`${BASE}/${encodeURIComponent(id)}/photo`, {
    method: "DELETE",
  });
  invalidateAll();
  recordAudit({
    action:     "location.delete_photo",
    entityType: "location",
    entityId:   id,
    before,
    after,
  });
  return after;
}

// ─── COST-TAB-BUILD: OpEx ───────────────────────────────────────────────────

/**
 * Fetch all OpEx budget rows for a location (all departments, all years).
 *
 * Returns { locationId, sourceMeta, departments } or throws a typed error.
 * sourceMeta is null when no rows exist for this location (clean empty state).
 *
 * @param {string} locationId
 * @returns {Promise<{locationId:string, sourceMeta:object|null, departments:Array}>}
 */
export async function getLocationOpex(locationId) {
  assertValidId("getLocationOpex", locationId);
  return jsonRequest(
    `${BASE}/${encodeURIComponent(locationId)}/opex`,
    { method: "GET" },
  );
}

/**
 * CAPACITY-SCHEDULE-S1: Fetch the full schedule + derived summary for a
 * single location.  Returns { rows: [...], summary: {...} }.
 */
export async function getCapacitySchedule(locationId) {
  assertValidId("getCapacitySchedule", locationId);
  return jsonRequest(
    `${BASE}/${encodeURIComponent(locationId)}/capacity-schedule`,
    { method: "GET" },
  );
}

/**
 * CAPACITY-SCHEDULE-S1: POST a new schedule row.
 * payload: { effectiveDate, installedSeats, maxDensity?, note? }
 * Returns the created row.
 */
export async function addCapacityScheduleRow(locationId, payload) {
  assertValidId("addCapacityScheduleRow", locationId);
  const row = await jsonRequest(
    `${BASE}/${encodeURIComponent(locationId)}/capacity-schedule`,
    { method: "POST", body: JSON.stringify(payload) },
  );
  invalidateAll();
  emitCommandIndexStale();
  return row;
}

/**
 * CAPACITY-SCHEDULE-S1: DELETE a single schedule row by id.
 */
export async function removeCapacityScheduleRow(locationId, rowId) {
  assertValidId("removeCapacityScheduleRow", locationId);
  const result = await jsonRequest(
    `${BASE}/${encodeURIComponent(locationId)}/capacity-schedule/${encodeURIComponent(rowId)}`,
    { method: "DELETE" },
  );
  invalidateAll();
  emitCommandIndexStale();
  return result;
}

/**
 * CAPACITY-SCHEDULE-S2: PATCH an existing schedule row.
 * patch: { effectiveDate?, installedSeats?, maxDensity?, note? }
 * Returns the updated row.
 */
export async function editCapacityScheduleRow(locationId, rowId, patch) {
  assertValidId("editCapacityScheduleRow", locationId);
  const row = await jsonRequest(
    `${BASE}/${encodeURIComponent(locationId)}/capacity-schedule/${encodeURIComponent(rowId)}`,
    { method: "PATCH", body: JSON.stringify(patch) },
  );
  invalidateAll();
  emitCommandIndexStale();
  return row;
}

/**
 * OFFICE-HEADCOUNT-MANUAL-OVERRIDE: Fetch the full headcount schedule for a
 * single location.  Returns { rows: [...] }.
 */
export async function getHeadcountSchedule(locationId) {
  assertValidId("getHeadcountSchedule", locationId);
  return jsonRequest(
    `${BASE}/${encodeURIComponent(locationId)}/headcount-schedule`,
    { method: "GET" },
  );
}

/**
 * OFFICE-HEADCOUNT-MANUAL-OVERRIDE: POST a new headcount schedule row.
 * payload: { effectiveDate, totalCount, note?, fullTimeCount?,
 *   vendorContractorCount?, independentContractorCount? }
 * Returns the created row (source will be 'manual').
 */
export async function addHeadcountScheduleRow(locationId, payload) {
  assertValidId("addHeadcountScheduleRow", locationId);
  const row = await jsonRequest(
    `${BASE}/${encodeURIComponent(locationId)}/headcount-schedule`,
    { method: "POST", body: JSON.stringify(payload) },
  );
  invalidateAll();
  return row;
}

/**
 * OFFICE-HEADCOUNT-MANUAL-OVERRIDE: DELETE a single headcount schedule row by id.
 */
export async function removeHeadcountScheduleRow(locationId, rowId) {
  assertValidId("removeHeadcountScheduleRow", locationId);
  const result = await jsonRequest(
    `${BASE}/${encodeURIComponent(locationId)}/headcount-schedule/${encodeURIComponent(rowId)}`,
    { method: "DELETE" },
  );
  invalidateAll();
  return result;
}

/**
 * OFFICE-HEADCOUNT-MANUAL-OVERRIDE: PATCH an existing headcount schedule row.
 * patch: { effectiveDate?, totalCount?, note?, fullTimeCount?,
 *   vendorContractorCount?, independentContractorCount? }
 * Returns the updated row (source will be stamped 'manual').
 */
export async function editHeadcountScheduleRow(locationId, rowId, patch) {
  assertValidId("editHeadcountScheduleRow", locationId);
  const row = await jsonRequest(
    `${BASE}/${encodeURIComponent(locationId)}/headcount-schedule/${encodeURIComponent(rowId)}`,
    { method: "PATCH", body: JSON.stringify(patch) },
  );
  invalidateAll();
  return row;
}

// Re-export the error classes so callers that import the client also get
// the error types from a single seam.
export {
  AuthError,
  ConflictError,
  NetworkError,
  NotFoundError,
  ValidationError,
};
